
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Consultation.css';
import { initializeTracker } from '../tracker_script.js';
import SEO from './SEO';

const Consultation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    discussion: '',
    additional: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });
  const [honeypot, setHoneypot] = useState('');
  const formLoadTime = useRef(Date.now());

  useEffect(() => {
    // Add meta tags to head
    const usernameMeta = document.createElement('meta');
    usernameMeta.name = 'username';
    usernameMeta.content = 'user_2o6eptKfyuvEOe6kSTFVnGzxmMU';
    document.head.appendChild(usernameMeta);

    const offerMeta = document.createElement('meta');
    offerMeta.name = 'offer';
    offerMeta.content = 'Consultation';
    document.head.appendChild(offerMeta);

    // Initialize the tracker
    try {
      initializeTracker();
      console.log('Tracker initialized successfully');
    } catch (error) {
      console.error('Error initializing tracker:', error);
    }

    // Cleanup function
    return () => {
      if (document.querySelector('meta[name="username"]')) {
        document.querySelector('meta[name="username"]').remove();
      }
      if (document.querySelector('meta[name="offer"]')) {
        document.querySelector('meta[name="offer"]').remove();
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bot traps: honeypot filled or submitted too fast
    if (honeypot || Date.now() - formLoadTime.current < 3000) {
      return;
    }

    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      const response = await fetch('https://expater-server.vercel.app/api/consultation-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: 'Consultation Request'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      // Redirect to thank you page instead of showing success message
      navigate('/consultation-thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({ 
        submitting: false, 
        submitted: false, 
        error: 'There was an error submitting your request. Please try again later.'
      });
    }
  };

  return (
    <div className="consultation-container">
      <SEO
        title="Book Consultation with Tuomas"
        description="Book a one-on-one consultation with Tuomas. Expert advice on expat relocation, UAE company setup, Dubai Golden Visa, citizenship by investment, and international tax optimization strategies."
        keywords="Tuomas Kivioja consultation, expat relocation consultant, UAE setup consultation, Dubai visa consultant, international tax consultation, citizenship by investment advisor, relocation strategy"
        canonical="https://tuomaskivioja.com/consultation"
        ogImage="https://tuomaskivioja.com/tuomas.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Personal Consultation",
          "provider": {
            "@type": "Person",
            "name": "Tuomas Kivioja",
            "jobTitle": "International Relocation Specialist"
          },
          "description": "One-on-one strategy sessions for expat relocation and tax optimization",
          "offers": {
            "@type": "Offer",
            "price": "400",
            "priceCurrency": "USD",
            "description": "60-minute consultation with follow-up email support"
          },
          "url": "https://tuomaskivioja.com/consultation"
        }}
      />
      <h2>One-on-one Consulting with Tuomas</h2>
      <p className="consultation-intro">I have been in the offshore world for 4 years. I can help you with: deciding where to form a company, residency options around the world, form the best offshore strategy to minimize your tax, offshore banking & everything related.</p>

        <div className="consultation-form-section">
          <h3>My guarantee is that you will receive all the information you need in one meeting. I will not ask you to book more time to get more information.</h3>
          <p>Please fill out this form and I will reach out within 24 hours to arrange a time.</p>
          
          <div className="consultation-package">
            <h4>📋 60-Minute Consultation + Written Report:</h4>
            <h4>$400</h4>
            <div className="package-details">
              <div className="package-item">
                <span className="package-icon">📞</span>
                <div className="package-text">
                  <strong>60-minute focused consultation</strong>
                  <p>Direct discussion on your specific situation, goals, and questions</p>
                </div>
              </div>
              <div className="package-item">
                <span className="package-icon">📧</span>
                <div className="package-text">
                  <strong>Follow-up email support included</strong>
                  <p>If I cannot answer all your questions during the session, or I need to do additional research, I will provide additional responses by email at no extra cost to make sure you are satisfied</p>
                </div>
              </div>
              <div className="package-item">
                <span className="package-icon">📄</span>
                <div className="package-text">
                  <strong>Written report included</strong>
                  <p>A written report that includes all my recommendations & research, so you have a clear reference to act on after the call</p>
                </div>
              </div>
            </div>
          </div>
          {formStatus.submitted ? (
            <div className="success-message">
              <h3>Thank you for your submission!</h3>
              <p>I will confirm if I can help you & if so, reach out to arrange a time within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="consultation-form">
              {/* Honeypot — hidden from real users, bots fill it in */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="discussion">What are you looking to discuss regarding international living, residency & citizenship options or company setup? Please explain as much detail as possible *</label>
                <textarea
                  id="discussion"
                  name="discussion"
                  value={formData.discussion}
                  onChange={handleChange}
                  rows="6"
                  placeholder="Please provide details about your current situation, goals, timeline, and any specific questions you have..."
                  required
                ></textarea>
              </div>
              
              {formStatus.error && (
                <div className="error-message">
                  {formStatus.error}
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={formStatus.submitting}
              >
                {formStatus.submitting ? 'Submitting...' : 'Submit & Book Consultation'}
              </button>

              <div className="pricing-reminder">
              <p><i><b>Note: Tuomas is not a tax or investment advisor and cannot provide official tax or investment advice.</b></i></p>
              <p><strong>In case of more complex situations, I can offer a custom quote based on your needs.</strong></p>
              </div>
            </form>
          )}
        </div>
    </div>
  );
};

export default Consultation;
